import React, { useMemo, useEffect, useState } from "react";
import { connect } from "react-redux";
import "./InterviewSidebar.scss";
import { InterviewSidebarEntry } from "./Sections/InterviewSidebarEntry";
import { InterviewSidebarContractConsent } from "./Sections/InterviewSidebarContractConsent";
import { InterviewSidebarCoverage } from "./Sections/InterviewSidebarCoverage";
import { InterviewSidebarPatientInfoScreening } from "./Sections/InterviewSidebarPatientInfoScreening";
import { InterviewSidebarHouseholdSection } from "./Sections/InterviewSidebarHouseholdSection";
import { InterviewSidebarPersonInfoScreening } from "./Sections/InterviewSidebarPersonInfoScreening";
import { InterviewSidebarSupplemental } from "./Sections/InterviewSidebarSupplemental";
import { InterviewSidebarDocumentation } from "./Sections/InterviewSidebarDocumentation";
import * as interviewWizardService from "../services/interviewWizardService";
import { InterviewSidebarExchange } from "./Sections/InterviewSidebarExchange";
import * as contractService from "../../../services/contractService";
import { isCallCenterUser } from "../../../services/userService";
import { withUser } from "@coverme/identity";
import { InterviewSidebarNotes } from "./Sections/InterviewSidebarNotes";
import * as notesActions from "../../../actions/personNotesActions";
import { InterviewSidebarGovernment } from "./Sections/InterviewSidebarGovernment";
import { InterviewSidebarVerification } from "./Sections/InterviewSidebarVerification";
import { InterviewSidebarLongTermCare } from "./Sections/InterviewSidebarLongTermCare";
import { InterviewSidebarFormsAndApplications } from "./Sections/InterviewSidebarFormsAndApplications";
import { InterviewSidebarTags } from "./Sections/InterviewSidebarTags";
import Axios from "axios";
import { some } from "lodash";
import * as tagsAction from "../../../actions/tagsActions";

const InterviewSidebar = ({
    interview,
    currentSection,
    documents,
    configuration,
    lookups,
    assistanceCategories,
    user,
    person,
    customer,
    notes,
    addNote,
    getNotes,
    customerCode,
    customerFeatures,
    deleteTag,
    addTag,
}) => {
    const { wizard } = interview;
    const patientAge = interview.entry?.patient?.age;
    const patientGender = interview.entry?.patient.gender;
    const sectionsToShow = interviewWizardService.getSectionsToBeShown(
        interview,
        configuration,
        assistanceCategories,
        user,
        person,
        customer
    );
    const [encounterTag, setEncounterTag] = useState("");

    const tagOptions = useMemo(() => {
        let noneOption = { label: "None", value: "None" };
        let result = lookups?.manualTags?.map(x => ({ label: x.tagName, value: x.id }));
        result.push(noneOption);
        if (
            interview?.encounterTag !== "" &&
            interview?.encounterTag !== null &&
            interview?.encounterTag !== undefined &&
            lookups?.automatedTags !== undefined &&
            lookups?.automatedTags !== null
        ) {
            let encounterTagOption = lookups?.automatedTags
                ?.filter(x => x.id === interview?.encounterTag)
                ?.map(x => ({ label: x.tagName, value: x.id }));

            if (encounterTagOption !== null && encounterTagOption !== undefined && encounterTagOption.length > 0) {
                result.push({ label: encounterTagOption[0].label, value: encounterTagOption[0].value });
            }
        }
        return result;
    }, [lookups?.manualTags, lookups?.automatedTags, interview.encounterTag]);

    useEffect(() => {
        Axios.get(`/api/encounter/${interview.encounterId}/tag`).then(data => setEncounterTag(data.data));
    }, []);

    return (
        <div className="interview-sidebar open">
            <InterviewSidebarEntry
                entry={interview?.entry}
                currentSection={currentSection}
                sectionName="entry"
                wizardStatus={wizard}
            />
            {contractService.shouldShowContractConsent(interview) && !isCallCenterUser(user) && (
                <InterviewSidebarContractConsent
                    contractConsent={contractService.getEffectiveContractConsent(interview)}
                    currentSection={currentSection}
                    sectionName="contractConsent"
                    wizardStatus={wizard}
                />
            )}
            <InterviewSidebarVerification
                currentSection={currentSection}
                sectionName="verification"
                wizardStatus={wizard}
                verificationResults={interview.verifications}
            />
            <InterviewSidebarCoverage
                coverage={interview?.coverage}
                currentSection={currentSection}
                sectionName="coverage"
                wizardStatus={wizard}
            />
            {sectionsToShow.patientInfoScreening && (
                <InterviewSidebarPatientInfoScreening
                    patientInfoScreening={interview.patientInfoScreening}
                    patientAge={patientAge}
                    maxChildAge={configuration.maxChildAge}
                    minFosterCareAge={configuration.minFosterCareAge}
                    patientGender={patientGender}
                    maxFosterCareAge={configuration.maxFosterCareAge}
                    currentSection={currentSection}
                    sectionName="patientInfoScreening"
                    wizardStatus={wizard}
                />
            )}
            {sectionsToShow.householdMatrix && (
                <InterviewSidebarHouseholdSection
                    householdMembers={interview.householdMembers}
                    patientName={interview?.entry?.patient?.fullName}
                    currentSection={currentSection}
                    sectionName="householdMatrix"
                    wizardStatus={wizard}
                />
            )}
            {sectionsToShow.personInfoScreening && (
                <InterviewSidebarPersonInfoScreening
                    householdMembers={interview.householdMembers}
                    entry={interview?.entry}
                    personInfoScreening={interview?.personInfoScreening}
                    currentSection={currentSection}
                    sectionName="personInfoScreening"
                    wizardStatus={wizard}
                />
            )}
            {sectionsToShow.government && (
                <InterviewSidebarGovernment
                    householdMembers={interview.householdMembers}
                    liabilities={interview?.liabilities}
                    entry={interview?.entry}
                    personInfoScreening={interview?.personInfoScreening}
                    currentSection={currentSection}
                    sectionName="government"
                    wizardStatus={wizard}
                />
            )}
            {sectionsToShow.longTermCare && (
                <InterviewSidebarLongTermCare
                    currentSection={currentSection}
                    sectionName="longTermCare"
                    wizardStatus={wizard}
                />
            )}
            {sectionsToShow.exchange && (
                <InterviewSidebarExchange
                    currentSection={currentSection}
                    sectionName="exchange"
                    wizardStatus={wizard}
                />
            )}
            {sectionsToShow.supplemental && (
                <InterviewSidebarSupplemental
                    currentSection={currentSection}
                    sectionName="supplemental"
                    wizardStatus={wizard}
                />
            )}
            {sectionsToShow.documentation && person?.globalId != null && (
                <InterviewSidebarDocumentation
                    documents={documents}
                    patientId={interview?.entry?.patient?.personId}
                    currentSection={currentSection}
                    sectionName="documentation"
                    wizardStatus={wizard}
                    isSectionOpen={true}
                />
            )}
            {person?.globalId != null && (
                <InterviewSidebarNotes
                    notes={notes}
                    personId={person?.globalId}
                    customerCode={customerCode}
                    addNote={addNote}
                    getNotes={getNotes}
                    currentSection={currentSection}
                    sectionName="notes"
                    wizardStatus={wizard}
                    isSectionOpen={true}
                />
            )}

            {some(customerFeatures, x => x === "Tags") === true && (
                <InterviewSidebarTags
                    encounterId={interview.encounterId}
                    currentSection={currentSection}
                    sectionName="Tag"
                    wizardStatus={wizard}
                    isSectionOpen={true}
                    tagOptions={tagOptions}
                    automatedTags={lookups?.automatedTags}
                    manualTags={lookups?.manualTags}
                    encounterTag={interview.encounterTag}
                    deleteTag={deleteTag}
                    addTag={addTag}
                />
            )}

            {sectionsToShow.formsAndApplications && person?.globalId != null && (
                <InterviewSidebarFormsAndApplications
                    currentSection={currentSection}
                    sectionName="formsAndApplications"
                    wizardStatus={wizard}
                    isSectionOpen={true}
                    formSection={true}
                />
            )}
        </div>
    );
};

const InterviewSidebarWithUser = withUser(InterviewSidebar);

export const InterviewSidebarContainer = connect(
    state => {
        const patientId = state.interview?.entry?.patient?.personId;
        let underlyingPerson = null;
        if (patientId) {
            underlyingPerson = state.person.data[patientId];
        }
        return {
            interview: state.interview,
            documents: state.documents,
            configuration: state.interviewLookups.data?.configuration || {},
            lookups: state.interviewLookups.data || {},
            assistanceCategories: state.assistanceCategories.data,
            person: underlyingPerson,
            customer: state.customer?.data,
            customerCode: state.customer.data?.customer?.code,
            notes: state.notes.data[underlyingPerson?.globalId],
            customerFeatures: state.customer?.data?.features,
        };
    },
    dispatch => ({
        getNotes: (customerCode, personId) => dispatch(notesActions.getNotes(customerCode, personId)),
        addNote: (customerCode, personId, text) => dispatch(notesActions.addNote(customerCode, personId, text)),
        deleteTag: encounterId => dispatch(tagsAction.deleteTag(encounterId)),
        addTag: (encounterId, tag) => dispatch(tagsAction.addTag(encounterId, tag)),
    })
)(InterviewSidebarWithUser);
